'use client';

import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../../config/api';
import { getSectionByKey } from '../../services/cmsService';
import { getProducts } from '../../services/productService';
import { handleImageError } from '../../utils/imageUtils';
import './FeaturedCollection.css';

const parseFeaturedItems = (data) => {
  if (data?.content?.items && data.content.items.length > 0) {
    return data.content.items
      .filter(item => item.isActive !== false)
      .map(item => ({
        _id: item.productId || item.id,
        name: item.title,
        image: getImageUrl(item.image)
      }));
  }
  return [];
};

const FeaturedCollection = ({ initialData }) => {
  const initialItems = parseFeaturedItems(initialData);
  const [sectionHeader, setSectionHeader] = useState({ 
    heading: initialData?.content?.heading || 'Featured Collection', 
    subheading: initialData?.content?.subheading || '' 
  });
  const [products, setProducts] = useState(initialItems);
  const [loading, setLoading] = useState(initialItems.length === 0);

  useEffect(() => {
    if (initialData?.content?.items && initialData.content.items.length > 0) {
      return;
    }
    fetchFeaturedProducts();
  }, [initialData]);

  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true);
      // 1. Try fetching CMS section
      const section = await getSectionByKey('featured_collection');
      if (section?.content?.items && section.content.items.length > 0) {
        if (section.content.heading) {
          setSectionHeader({ heading: section.content.heading, subheading: section.content.subheading || '' });
        }
        const activeItems = section.content.items
          .filter(item => item.isActive !== false)
          .map(item => ({
            _id: item.productId || item.id,
            name: item.title,
            image: getImageUrl(item.image)
          }));

        if (activeItems.length > 0) {
          setProducts(activeItems);
          return;
        }
      }

      // 2. Fallback to products from Firestore
      const result = await getProducts({});
      let fetchedProducts = result.products || [];
      const featured = fetchedProducts.filter(p => p.featured);
      const displayList = featured.length > 0 ? featured : fetchedProducts;
      setProducts(displayList.slice(0, 5).map(p => ({
        _id: p._id || p.id,
        name: p.name,
        image: getImageUrl(p.images?.[0] || p.image)
      })));
    } catch (error) {
      console.error('Error fetching featured products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const staticProducts = [
    {
      _id: 'hero',
      name: 'HANDLOOM SILK SAREE',
      image: '/img/0952.webp'
    },
    {
      _id: '1',
      name: 'VELVET BRIDAL LEHENGA',
      image: '/img/06e447550bc57e1226507591a9b847e5.jpg'
    },
    {
      _id: '2',
      name: 'KIDS LEHENGA SET',
      image: '/img/Attached_image.png'
    },
    {
      _id: '3',
      name: 'BANARASI DUPATTA',
      image: '/img/lehenga-2.jpg'
    },
    {
      _id: '4',
      name: 'ROYAL LEHENGA SET',
      image: '/img/06e447550bc57e1226507591a9b847e5.jpg'
    }
  ];

  if (loading) {
    return (
      <section className="featured-collection-section">
        <h2 className="common-heading center">Featured Collection</h2>
        <div className="featured-container" style={{ opacity: 0.6, minHeight: '300px' }}></div>
      </section>
    );
  }

  const displayProducts = products.length > 0 ? products : staticProducts;
  
  if (displayProducts.length === 0) return null;

  const heroProduct = displayProducts[0];
  const gridProducts = displayProducts.slice(1, 5);

  const getHeroImage = (product) => {
    const raw = product.image || product.images?.[0];
    return getImageUrl(raw, { w: 800, h: 1000, q: 80, f: 'auto' });
  };

  const getGridImage = (product) => {
    const raw = product.image || product.images?.[0];
    return getImageUrl(raw, { w: 400, h: 500, q: 80, f: 'auto' });
  };

  return (
    <section className="featured-collection-section">
      <h2 className="common-heading center">{sectionHeader.heading || 'Featured Collection'}</h2>
      {sectionHeader.subheading && <p className="section-subheading center" style={{ textAlign: 'center', marginBottom: '24px', opacity: 0.8 }}>{sectionHeader.subheading}</p>}
      <div className="featured-container">
        {/* Hero Card - Left side */}
        <div className="hero-card">
          <div className="hero-image">
            <img 
              src={getHeroImage(heroProduct)} 
              data-original-src={typeof (heroProduct.image || heroProduct.images?.[0]) === 'string' ? (heroProduct.image || heroProduct.images?.[0]) : ''}
              alt={heroProduct.name || 'Featured Collection'}
              loading="lazy"
              decoding="async"
              width="800"
              height="1000"
              onError={(e) => handleImageError(e)}
            />
            <div className="hero-image-overlay">
              <h3>{heroProduct.name || 'Featured Collection'}</h3>
            </div>
          </div>
        </div>

        {/* Featured Cards Grid - Right side (2x2) */}
        <div className="featured-grid">
          {gridProducts.map((product, index) => {
            const rawSrc = typeof (product.image || product.images?.[0]) === 'string' ? (product.image || product.images?.[0]) : '';
            return (
              <div 
                key={index} 
                className="featured-card"
              >
                <div className="featured-card-image">
                  <img 
                    src={getGridImage(product)}
                    data-original-src={rawSrc}
                    alt={product.name || `Featured Product ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    width="400"
                    height="500"
                    onError={(e) => handleImageError(e)}
                  />
                  <div className="featured-card-image-overlay">
                    <h3>{product.name || `Featured Product ${index + 1}`}</h3>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedCollection;